import { useContext } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { GapBox } from 'components/Box'
import { TransferList, useResetFormFieldStateOnFetch } from 'components/Form'
import { SectionHeader } from 'components/Header'
import Loading from 'components/Loading'
import Paper from 'components/Paper'
import { AddEditDomainContext } from 'features/inventory/domain/contexts/AddEditDomainContext'
import { useGetAdUnitsForPageTransferList } from 'features/inventory/page/forms/GeneralForm/hooks/useGetAdUnitsForPageTransferList'
import { useAbility } from 'providers/casl'
import { MAX_ACTIVE_AD_UNITS, Schema } from './schema'

export const AdUnitConnectionSection = (): JSX.Element => {
  const ability = useAbility()
  const { t } = useTranslation('features/domain', {
    keyPrefix: 'form.richPlacements.multiAnchor.adUnitConnection',
  })
  const { control } = useFormContext<Schema>()
  const cannotUpdate: boolean =
    ability.cannot('update', 'Domain') ||
    ability.cannot('update', 'DomainRichPlacementsFeature-multiAnchorAdUnitConnection')

  const { domain } = useContext(AddEditDomainContext)
  if (domain === undefined) {
    throw Error('Domain should be accessible')
  }
  const { domainAdUnits, isFetched, isLoading } = useGetAdUnitsForPageTransferList({
    domainId: domain.id,
  })

  useResetFormFieldStateOnFetch<Schema>('adUnitIds', isFetched)

  if (isLoading) {
    return <Loading />
  }

  return (
    <Paper
      data-cy='section-ad-unit-connection'
      variant='cream'
    >
      <SectionHeader
        subtitle={t('subtitle')}
        title={t('title')}
      />

      <GapBox />

      <TransferList<Schema>
        all={domainAdUnits}
        control={control}
        disabled={cannotUpdate}
        maxActive={MAX_ACTIVE_AD_UNITS}
        name='adUnitIds'
      />
    </Paper>
  )
}
